import { getOrganizationUnitIcon } from '@shared/utils/organization-unit-icon.util';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, switchMap, takeUntil, tap } from 'rxjs/operators';
import {
  GlobalSearchPersonResultDto,
  GlobalSearchResponseDto,
  GlobalSearchUnitResultDto,
  SearchApiService
} from '@core/api/services/search-api.service';

@Component({
  selector: 'app-global-search',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './global-search.component.html',
  styleUrl: './global-search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GlobalSearchComponent implements OnDestroy {
  readonly searchControl = new FormControl('', { nonNullable: true });
  results: GlobalSearchResponseDto = { people: [], units: [] };
  loading = false;
  open = false;
  mobileOpen = false;
  searched = false;

  @ViewChild('mobileInput') mobileInput?: ElementRef<HTMLInputElement>;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly searchApi: SearchApiService,
    private readonly router: Router,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>
  ) {
    this.searchControl.valueChanges.pipe(
      debounceTime(275),
      distinctUntilChanged(),
      tap(term => {
        const normalized = term.trim();
        this.searched = normalized.length >= 2;
        this.open = normalized.length >= 2;
        if (normalized.length < 2) {
          this.loading = false;
          this.results = { people: [], units: [] };
        }
        this.changeDetector.markForCheck();
      }),
      switchMap(term => {
        const normalized = term.trim();
        if (normalized.length < 2) {
          return of({ people: [], units: [] });
        }

        this.loading = true;
        this.changeDetector.markForCheck();

        return this.searchApi.search(normalized).pipe(
          catchError(() => of({ people: [], units: [] })),
          finalize(() => {
            this.loading = false;
            this.changeDetector.markForCheck();
          })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(results => {
      this.results = results;
      this.changeDetector.markForCheck();
    });
  }

  get hasResults(): boolean {
    return this.results.people.length > 0 || this.results.units.length > 0;
  }

  openDesktop(): void {
    if (this.searchControl.value.trim().length >= 2) {
      this.open = true;
    }
  }

  openMobile(): void {
    this.mobileOpen = true;
    this.open = true;
    this.changeDetector.markForCheck();
    setTimeout(() => this.mobileInput?.nativeElement.focus(), 0);
  }

  close(): void {
    this.open = false;
    this.mobileOpen = false;
    this.changeDetector.markForCheck();
  }

  clear(): void {
    this.searchControl.setValue('');
    this.results = { people: [], units: [] };
    this.searched = false;
    this.close();
  }

  navigateToPerson(person: GlobalSearchPersonResultDto): void {
    this.router.navigate(['/people', person.id]);
    this.clear();
  }

  navigateToUnit(unit: GlobalSearchUnitResultDto): void {
    this.router.navigate(['/units', unit.id]);
    this.clear();
  }

  getUnitIcon(unit: GlobalSearchUnitResultDto): string {
    return getOrganizationUnitIcon(unit.organizationLevelId, unit.levelName);
  }

  isSvgIcon(icon: string): boolean {
    return icon === 'church-group';
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    this.close();
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    if (event.target instanceof Node && !this.host.nativeElement.contains(event.target)) {
      this.close();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
